import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

const AUTHORITY_SITE = 'https://aiaccountabilitycouncil.com';

export default function declarationScope() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="declaration-scope-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <Link to="/" className="header-logo text-xl">AASC Registry</Link>
              <a 
                href={AUTHORITY_SITE}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-zinc-600 hover:text-zinc-900 flex items-center gap-1 border-l border-zinc-200 pl-6"
              >
                AI Accountability Standards Council <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/apply" className="nav-link">Apply</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12 flex-1">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Declaration Scope
          </h1>
          <p className="text-zinc-500 mb-8">AI Accountability Standards Council</p>
          
          <div className="bg-blue-50 border border-blue-200 p-6 rounded mb-8">
            <p className="text-blue-800 mb-4">
              Declaration scope and limitations are defined by the AI Accountability Standards Council. 
              This platform administers the declaration process; it does not define scope.
            </p>
            <a 
              href={`${AUTHORITY_SITE}/governance-and-authority`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-blue-700 font-medium hover:underline"
            >
              View Official Governance and Authority Scope <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <div className="prose prose-zinc max-w-none">
            <h2>Key Points</h2>
            
            <p>
              <strong>Accountability declaration confirms documented alignment</strong> with defined foundational 
              control standards at a point in time.
            </p>

            <h3>Accountability Declaration Does Not:</h3>
            <ul>
              <li>Guarantee performance</li>
              <li>Imply regulatory approval</li>
              <li>Transfer liability</li>
              <li>Constitute endorsement</li>
            </ul>

            <h3>Declaration Is:</h3>
            <ul>
              <li>Conditional</li>
              <li>Revocable</li>
              <li>Time-bound (12 months)</li>
              <li>Version-specific</li>
            </ul>

            <div className="bg-zinc-100 p-4 border-l-4 border-zinc-400 mt-6">
              <p className="font-medium">
                For complete declaration scope definitions, refer to the official authority site.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-zinc-200">
            <h3 className="font-medium mb-4">Official Resources</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <a 
                href={`${AUTHORITY_SITE}/standards`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
              >
                Standards Index <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href={`${AUTHORITY_SITE}/governance-and-authority`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
              >
                Governance <ExternalLink className="w-3 h-3" />
              </a>
              <a 
                href={`${AUTHORITY_SITE}/terms-of-service`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-600 hover:text-zinc-900 flex items-center gap-1"
              >
                Terms of Service <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </div>
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
